import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import classnames from "classnames";
import {differenceInDays, formatISO} from "date-fns";
import {useEffect} from "react";
import {useForm} from "react-hook-form";
import {useNavigate} from "react-router";
import * as Yup from "yup";

import Button from "@/components/button/Button.tsx";
import {useCheckoutContext, useCurrentUserContext} from "@/components/context/hooks.ts";
import {dateOnly} from "@/lib/format.tsx";
import {CancelSubscriptionResponse, GQL_CANCEL_SUBSCRIPTION} from "@/lib/queries/billing.ts";

export interface CancelSubscriptionFormValues {
  churn_reason_details: string;
}

const CancelSubscriptionFormSchema = Yup.object({
  churn_reason_details: Yup.string()
    .trim()
    .required("To help us improve, please provide a reason for cancelling your account."),
});

export const CancelSubscriptionForm = () => {
  const {user, brand} = useCurrentUserContext();
  const checkoutContext = useCheckoutContext();
  const navigateFn = useNavigate();
  const [cancelSubscription, {data}] = useMutation<CancelSubscriptionResponse>(GQL_CANCEL_SUBSCRIPTION);
  const {
    handleSubmit,
    formState: {errors},
    register,
    setError,
  } = useForm<CancelSubscriptionFormValues>({
    resolver: yupResolver<CancelSubscriptionFormValues>(CancelSubscriptionFormSchema),
  });
  const onSubmit = async (formData: CancelSubscriptionFormValues) => {
    const {data: result} = await cancelSubscription({
      variables: {
        churnReasonDetails: formData.churn_reason_details,
      },
    });
    if (result?.cancelSubscription?.errors) {
      return;
    }
    navigateFn("/billing/cancel/done");
  };

  const serverErrors = data?.cancelSubscription?.errors || {};

  // we're only displaying next billing date message if it's more than 2 days from now
  const subscriptionValidUntil =
    checkoutContext?.nextBillingDate && differenceInDays(checkoutContext.nextBillingDate, new Date()) > 2
      ? dateOnly(formatISO(checkoutContext.nextBillingDate), user!.current_date_format, user!.account!.timezone)
      : null;

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => {
          setError(key as keyof CancelSubscriptionFormValues, {message: serverErrors[key][0]});
        });
    }
  }, [setError, serverErrors]);
  return (
    <div className="row">
      <div className="col-12 col-sm-8">
        <div className="white-block white-block-border rounded p-4">
          {subscriptionValidUntil && (
            <p>
              Your current subscription to {brand?.name} is valid until {subscriptionValidUntil}.
            </p>
          )}
          <p>Your feedback matters. Please share why you are requesting to cancel your Uptime.com subscription.</p>
          <form onSubmit={handleSubmit(onSubmit, (errors) => console.log(errors))}>
            <div className="row mb-4">
              <div className="col-12">
                <textarea
                  className={classnames("form-control", {"is-invalid": errors?.churn_reason_details})}
                  rows={8}
                  {...register("churn_reason_details")}
                />
                {errors.churn_reason_details && (
                  <div className="invalid-feedback d-block">{errors.churn_reason_details?.message}</div>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12 d-flex justify-content-end">
                <Button type="submit">Request Cancellation</Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
